import { t } from "@lingui/macro";
import { Check, Lock, Star } from "@phosphor-icons/react";
import { Button } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { LocaleLink } from "@/client/components/locale-link";
import { LocaleSwitch } from "@/client/components/locale-switch";
import { Logo } from "@/client/components/logo";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { useAuthProviders } from "@/client/services/auth/providers";

import "./_components/auth-animations.css";
import { StylionAtmosphere } from "@/client/components/stylion-atmosphere";

import { AuthResumeAnimation } from "./_components/auth-resume-animation";
import { SocialAuth } from "./_components/social-auth";

const companyLogos = ["amazon", "google", "postman", "twilio", "zalando"];

const featureBadges = [
  { label: () => t`CV Verified by Stylion`, position: "top-[8%] left-[5%]" },
  { label: () => t`ATS Matching 98%`, position: "top-[18%] right-[5%]" },
  { label: () => t`Skills Verified`, position: "top-[38%] left-[8%]" },
  { label: () => t`Job Match Score`, position: "bottom-[35%] left-[5%]" },
  { label: () => t`Professional Templates`, position: "top-[35%] right-[3%]" },
  { label: () => t`Instant PDF Export`, position: "bottom-[22%] right-[10%]" },
];

export const AuthLayout = () => {
  const location = useLocation();
  const { providers } = useAuthProviders();
  const isAuthRoute = useMemo(
    () => /\/auth\/(login|register)$/.test(location.pathname),
    [location],
  );

  const effectiveProviders = providers ?? ["email"];
  const hideDivider = !effectiveProviders.includes("email") || effectiveProviders.length === 1;

  const isLogin = location.pathname.includes("/auth/login");

  return (
    <div className="stylion-app flex min-h-screen">
      {/* ─── Left Panel: Marketing atmosphere + showcase ─── */}
      <div className="relative hidden overflow-hidden lg:flex lg:w-[55%] xl:w-[58%]">
        <StylionAtmosphere />
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--stylion-sky)]/80 via-[var(--stylion-cream)] to-[var(--stylion-cta-from)]/30" />

        {/* Animated resume showcase with floating badges */}
        <div className="relative flex w-full items-center justify-center p-8">
          {/* Glow effect behind the animation */}
          <div className="auth-glow-pulse absolute left-1/2 top-[45%] size-[380px] rounded-full bg-primary/15 blur-3xl" />

          {/* 3D card flip animation reused from homepage */}
          <div className="auth-resume-animation relative z-[1] w-full max-w-[500px]">
            <AuthResumeAnimation />
          </div>

          {/* Floating feature badges — staggered entrance + gentle float */}
          {featureBadges.map(({ label, position }, index) => (
            <div
              key={position}
              className={cn("auth-badge-enter absolute z-[2]", position)}
              style={{ animationDelay: `${1.5 + index * 0.25}s` }}
            >
              <div
                className="auth-badge-float flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-medium text-gray-800 shadow-lg backdrop-blur-sm dark:bg-gray-800/90 dark:text-gray-200"
                style={{ animationDuration: `${3 + index * 0.4}s` }}
              >
                <Check className="size-3.5 text-emerald-600" weight="bold" />
                <span>{label()}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom section: trust strip + security badges */}
        <div className="auth-bottom-enter absolute inset-x-0 bottom-0 px-8 pb-6">
          {/* Trusted by */}
          <div className="mb-4 text-center">
            <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              {t`Trusted by professionals at`}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6">
              {companyLogos.map((company) => (
                <img
                  key={company}
                  className="max-h-5 object-contain opacity-50 grayscale transition-all hover:opacity-80 hover:grayscale-0"
                  src={`/brand-logos/light/${company}.svg`}
                  alt={company}
                  width={60}
                  height={20}
                />
              ))}
            </div>
          </div>

          {/* Security badges */}
          <div className="flex items-center justify-center gap-4">
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Lock className="size-3 text-accent" weight="fill" />
              {t`Your data is secure`}
            </span>
            <span className="text-border">·</span>
            <span className="text-[11px] text-muted-foreground">{t`256-bit SSL`}</span>
            <span className="text-border">·</span>
            <span className="text-[11px] text-muted-foreground">{t`GDPR Compliant`}</span>
          </div>
        </div>
      </div>

      {/* ─── Right Panel: Auth Form ─── */}
      <div className="stylion-app-shell flex flex-1 flex-col border-l border-black/5 lg:w-[45%] xl:w-[42%]">
        {/* Header: Logo + Language */}
        <div className="flex items-center justify-between px-6 pt-4">
          {/* Logo - appears on left in LTR, right in RTL */}
          <LocaleLink to="/" className="order-first rtl:order-last">
            <Logo size={120} />
          </LocaleLink>

          <div className="order-last flex items-center gap-2 rtl:order-first">
            <LocaleSwitch className="!size-6 !p-0 !border-none !bg-transparent hover:!bg-transparent focus:!ring-0 [&_img]:size-5 [&_img]:object-cover [&_img]:rounded-full" />
          </div>
        </div>

        {/* Centered form content */}
        <div className="flex flex-1 flex-col items-center justify-center px-8 py-10 sm:px-12 lg:px-14 xl:px-16">
          <div className="w-full max-w-sm">

            {/* Trust badge - enhanced for mobile */}
            <div className="mb-6 flex flex-col items-center gap-3">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                <Star className="size-3.5 fill-current" weight="fill" />
                {t`Trusted by 6M+ professionals`}
              </span>
              
              {/* Mobile security badges */}
              <div className="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400 lg:hidden">
                <span className="flex items-center gap-1">
                  <Lock className="size-3 text-emerald-500" weight="fill" />
                  {t`Secure`}
                </span>
                <span>·</span>
                <span>{t`256-bit SSL`}</span>
                <span>·</span>
                <span>{t`GDPR`}</span>
              </div>
            </div>

            {/* Heading */}
            <div className="mb-8 text-center">
              <h1 className="text-2xl font-bold tracking-tight text-[var(--stylion-ink)] sm:text-3xl">
                {isLogin ? t`Welcome Back` : t`Create Perfect Resume`}
              </h1>
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                {isLogin
                  ? t`Login to your Stylion account`
                  : t`Join millions building AI-powered resumes`}
              </p>
            </div>

            {/* Social auth + OR + Form */}
            {isAuthRoute && (
              <>
                <SocialAuth />

                <div className={cn("my-6 flex items-center gap-3", hideDivider && "hidden")}>
                  <hr className="flex-1 border-gray-200 dark:border-gray-700" />
                  <span className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    {t`or`}
                  </span>
                  <hr className="flex-1 border-gray-200 dark:border-gray-700" />
                </div>
              </>
            )}

            {/* The form (login or register) renders here */}
            <Outlet />

            {/* Footer links */}
            {isAuthRoute && (
              <div className="mt-8 space-y-5 text-center">
                {/* Mobile trust indicators */}
                <div className="mb-4 flex flex-wrap items-center justify-center gap-5 text-[11px] text-gray-500 dark:text-gray-400 lg:hidden">
                  <span className="flex items-center gap-1.5">
                    <Check className="size-3 text-emerald-500" weight="bold" />
                    {t`Free Forever`}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Check className="size-3 text-emerald-500" weight="bold" />
                    {t`No Credit Card`}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Check className="size-3 text-emerald-500" weight="bold" />
                    {t`ATS Optimized`}
                  </span>
                </div>

                <div className="flex flex-col items-center gap-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {isLogin ? t`Don't have an account?` : t`Already have an account?`}
                  </p>
                  <Button
                    asChild
                    variant="outline"
                    className="h-11 w-full rounded-xl border-2 border-primary text-sm font-semibold text-primary hover:bg-primary/5"
                  >
                    <LocaleLink to={isLogin ? "/auth/register" : "/auth/login"}>
                      {isLogin ? t`Create Free Account` : t`Log In`}
                    </LocaleLink>
                  </Button>
                </div>

      
              </div>
            )}
          </div>
        </div>

        {/* Footer: theme + V1 + terms */}
        <div className="px-6 pb-4 text-center">
          <div className="mb-4 flex justify-center">
            <ThemeSwitch />
          </div>
          <p className="mb-1 text-xs text-gray-400 dark:text-gray-500">{t`Looking for the classic version?`}</p>
          <a
            href="https://app.stylion.io"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            {t`Classic Resume Builder`}
            <svg className="size-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>
          <p className="text-[11px] leading-relaxed text-gray-400 dark:text-gray-500 mt-3">
                  {t`By signing up, you agree to our`}{" "}
                  <LocaleLink
                    to="/meta/terms-of-use"
                    className="font-medium underline underline-offset-2 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {t`Terms of Service`}
                  </LocaleLink>{" "}
                  {t`and`}{" "}
                  <LocaleLink
                    to="/meta/privacy-policy"
                    className="font-medium underline underline-offset-2 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {t`Privacy Policy`}
                  </LocaleLink>
                </p>
        </div>
      </div>
    </div>
  );
};
